/**
 * Perspective Challenges for Trump Cards
 * 
 * When a player plays a trump card, they face a "Perspective Challenge"
 * showing competing Lithuanian and Belarusian historical interpretations.
 * 
 * Extracted from durak_history.html lines 1202-1230
 */

import type { Perspective } from '../types/question';

export const PERSPECTIVES: Perspective[] = [
  {
    topic: 'Vytautas the Great / Vitaut',
    lt: 'Vytautas the Great — Lithuanian national hero who expanded the Grand Duchy to the Black Sea. Defender of Lithuanian statehood and one of the most powerful rulers in 15th-century Europe.',
    be: 'Vitaut — ruler of a Belarusian-Lithuanian state; his court used Old Belarusian as the official administrative language.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'National Academy of Sciences of Belarus',
    neutral: 'Both narratives reflect real aspects of his reign. The GDL was a multiethnic, multilingual state. The debate reflects post-1991 nation-building processes.',
    sourceQuote: '"Vytautas brought the principality to its greatest extent." — Rowell, S.C., Lithuania Ascending, Cambridge UP, 1994',
    correctAnswer: 2
  },
  {
    topic: 'Grand Duchy of Lithuania Identity',
    lt: 'A Lithuanian state that expanded eastward, incorporating Ruthenian lands under Lithuanian leadership.',
    be: 'A Belarusian-Lithuanian state where Old Belarusian was the administrative language and Belarusians formed the majority population.',
    ltSource: 'Lithuanian Institute of History (2020)',
    beSource: 'NAS Belarus, Institute of History (2019)',
    neutral: 'The GDL was a multiethnic polity; modern national categories do not map cleanly onto its identity.',
    sourceQuote: '"The Grand Duchy defies simple national categorisation." — Snyder, T., The Reconstruction of Nations, Yale UP (2003)',
    correctAnswer: 0
  },
  {
    topic: 'Francysk Skaryna',
    lt: 'An important printer and humanist figure of the Grand Duchy of Lithuania.',
    be: 'The father of Belarusian literature and printing; first to publish books in the Belarusian vernacular.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'NAS Belarus (2019)',
    neutral: 'Skaryna\'s work served multiple linguistic communities; his legacy transcends single national claims.',
    sourceQuote: '"Skaryna\'s Bible translation represents the first major work of Slavic vernacular printing." — Europeana (CC0)',
    correctAnswer: 1
  },
  {
    topic: 'Battle of Grunwald (1410)',
    lt: 'A decisive Lithuanian-Polish victory led by Vytautas, securing Lithuanian independence from the Teutonic Knights.',
    be: 'A Belarusian-Lithuanian-Polish victory where Ruthenian regiments played a crucial role under Vitaut\'s command.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'NAS Belarus (2020)',
    neutral: 'The battle united Polish, Lithuanian, and Ruthenian forces against the Teutonic Order, reshaping medieval Eastern Europe.',
    sourceQuote: '"The battle marked the end of the Teutonic Knights\' expansion and established the GDL as a major power." — Britannica',
    correctAnswer: 2
  },
  {
    topic: 'Mindaugas Coronation (1253)',
    lt: 'Mindaugas became the first and only crowned King of Lithuania, establishing the Lithuanian state in Western Christendom.',
    be: 'A tactical baptism for political gain; the majority of the population remained pagan and the state was not yet fully formed.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'NAS Belarus (2019)',
    neutral: 'Mindaugas\s coronation was a pragmatic political move to gain papal recognition and halt Crusader attacks.',
    sourceQuote: '"Mindaugas accepted baptism in 1251 and was crowned in 1253, but later renounced Christianity." — Rowell (1994)',
    correctAnswer: 2
  },
  {
    topic: 'Union of Lublin (1569)',
    lt: 'A voluntary union between equals that gave Lithuania access to the Polish Crown and protected it from Muscovy.',
    be: 'The beginning of Polish cultural and political domination that marginalized Belarusian-Lithuanian traditions.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'NAS Belarus (2020)',
    neutral: 'The union created the largest state in Europe but gradually shifted power toward Poland and led to the loss of distinct institutions.',
    sourceQuote: '"The Union of Lublin created the Polish-Lithuanian Commonwealth, the largest state in early modern Europe." — Davies',
    correctAnswer: 2
  },
  {
    topic: 'January Uprising (1863-1864)',
    lt: 'A Polish-Lithuanian uprising against Russian rule, with Lithuanian participants fighting for autonomy.',
    be: 'A Belarusian-Polish-Lithuanian uprising where Belarusian peasants and gentry fought against Russian imperialism.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'NAS Belarus (2018)',
    neutral: 'The uprising united nobles and peasants across ethnic lines against Tsarist oppression, though it lacked unified leadership.',
    sourceQuote: '"The January Uprising was the largest 19th-century rebellion against the Russian Empire." — Britannica',
    correctAnswer: 1
  },
  {
    topic: 'Belarusian People\'s Republic (1918)',
    lt: 'A short-lived German puppet state that emerged during WWI chaos; Lithuania recognized it briefly.',
    be: 'The first Belarusian state in modern history, declared independent but crushed by Soviet Russia and Poland.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'NAS Belarus (2020)',
    neutral: 'The BNR existed for less than a year but established important precedents for Belarusian statehood claims.',
    sourceQuote: '"The Belarusian People\'s Republic declared independence on March 25, 1918." — Belarusian academic sources',
    correctAnswer: 1
  },
  {
    topic: 'Molotov-Ribbentrop Pact (1939)',
    lt: 'A secret protocol divided Eastern Europe between Nazi Germany and the USSR, leading to Soviet occupation of Lithuania.',
    be: 'The pact placed Belarusian territories under Soviet control, reuniting Belarusian lands but at the cost of independence.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'NAS Belarus (2019)',
    neutral: 'The secret protocols divided the region into spheres of influence, leading to Soviet annexation of the Baltic states and eastern Poland.',
    sourceQuote: '"The secret protocol divided Eastern Europe into German and Soviet spheres of influence." — Snyder, Bloodlands',
    correctAnswer: 2
  },
  {
    topic: 'Chernobyl Disaster (1986)',
    lt: 'A Soviet nuclear catastrophe that affected Lithuania through radiation, accelerating anti-Soviet sentiment.',
    be: 'The disaster contaminated 70% of Belarusian territory, causing long-term health and environmental damage still felt today.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'NAS Belarus (2020)',
    neutral: 'Chernobyl was a turning point that exposed Soviet incompetence and fueled independence movements across the republics.',
    sourceQuote: '"Belarus received 70% of the radioactive fallout from Chernobyl." — UN reports',
    correctAnswer: 1
  },
  {
    topic: 'Restoration of Independence (1990-1991)',
    lt: 'Lithuania restored its independent statehood based on the 1918 Act of Independence, rejecting Soviet rule.',
    be: 'Belarus declared sovereignty but conservative forces blocked full independence until after the failed August 1991 coup.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'NAS Belarus (2020)',
    neutral: 'Both nations took different paths to independence, with Lithuania moving quickly and Belarus hesitating until the Soviet collapse.',
    sourceQuote: '"Lithuania declared independence on March 11, 1990; Belarus followed on August 25, 1991." — Academic sources',
    correctAnswer: 2
  },
  {
    topic: 'Kievan Rus Legacy',
    lt: 'The Grand Duchy of Lithuania succeeded Kievan Rus as the dominant power in Eastern Europe, inheriting its traditions.',
    be: 'Kievan Rus was a Belarusian-Ukrainian state; Lithuanian rulers married into Ruthenian nobility to legitimize their rule.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'NAS Belarus (2019)',
    neutral: 'The GDL absorbed much of Rus\' territory and culture, with Ruthenian becoming the state language for centuries.',
    sourceQuote: '"The Grand Duchy of Lithuania became the principal heir to Kievan Rus in the 14th century." — Rowell (1994)',
    correctAnswer: 2
  },
  {
    topic: 'Statutes of the GDL',
    lt: 'Lithuanian legal codes written in Ruthenian for practical administrative purposes, reflecting Lithuanian statehood.',
    be: 'Belarusian-Lithuanian legal codes written in the language of the majority population, showing the state\'s Belarusian character.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'NAS Belarus (2020)',
    neutral: 'The Statutes represent a unique legal tradition that blended Rus\' law, Lithuanian customs, and Polish influences.',
    sourceQuote: '"The Third Statute of 1588 remained in force until 1840." — Lithuanian and Belarusian sources',
    correctAnswer: 2
  },
  {
    topic: 'Polonization in the Commonwealth',
    lt: 'Lithuanian nobility adopted Polish culture voluntarily to maintain status in the unified state.',
    be: 'Both Belarusian and Lithuanian cultures were marginalized as Polish language and culture dominated the Commonwealth.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'NAS Belarus (2019)',
    neutral: 'Polonization was a gradual process affecting the nobility across the Commonwealth, leading to loss of native linguistic traditions.',
    sourceQuote: '"By the 18th century, Polish had become the language of the nobility throughout the Commonwealth." — Snyder',
    correctAnswer: 2
  },
  {
    topic: 'Soviet Industrialization',
    lt: 'Forced industrialization brought modern infrastructure but at the cost of traditional Lithuanian rural society.',
    be: 'Massive industrial projects transformed Belarus from an agrarian to an industrial society, with both benefits and trauma.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'NAS Belarus (2020)',
    neutral: 'Soviet industrialization modernized both republics but involved forced collectivization, repression, and environmental damage.',
    sourceQuote: '"Soviet industrialization created modern cities but at enormous human cost." — Academic consensus',
    correctAnswer: 2
  },
  {
    topic: '2020 Election Protests',
    lt: 'The mass protests following the fraudulent August 2020 election represent a genuine Belarusian democratic revolution that deserves full international support, including sanctions against the Lukashenko regime.',
    be: 'The 2020 uprising expressed the will of the Belarusian nation for self-determination; the subsequent repression and forced exile of hundreds of thousands constitutes a crime against Belarusian statehood.',
    ltSource: 'Lithuanian Foreign Ministry Statement, August 2020',
    beSource: 'Viasna Human Rights Centre, 2020–2023 Reports',
    neutral: 'The 2020 protests were the largest in Belarusian history; the government response involved over 35,000 arrests, systematic torture, and forced emigration of an estimated 200,000–500,000 people.',
    sourceQuote: '"What happened in Belarus in 2020 was not a failed revolution but a suspended one." — Hanna Liubakova, journalist',
    correctAnswer: 0
  },
  {
    topic: 'Language Policy and Russification',
    lt: 'The marginalisation of the Belarusian language through Soviet policy and the 1995 referendum represents a form of cultural colonialism that undermines Belarusian nationhood — similar to Russification pressures Lithuania resisted.',
    be: 'Restoring the Belarusian language to everyday public life is inseparable from democratic and national revival; language is the core of Belarusian identity that survived Soviet suppression.',
    ltSource: 'Laitin, D. Identity in Formation. Cornell UP, 1998',
    beSource: 'Zaprudnik, J. Belarus: At a Crossroads in History. Westview, 1993',
    neutral: 'While Russian dominates in Belarusian cities, the Belarusian language has seen a revival among young educated urbanites since 2010, particularly as a marker of opposition identity.',
    sourceQuote: '"Speaking Belarusian has become an act of political resistance." — Orsha, activist, 2021',
    correctAnswer: 1
  },
  {
    topic: 'Adam Mickiewicz / Adam Mickievič',
    lt: 'Adam Mickevičius — Polish-language Romantic poet whose works are central to the Lithuanian national imagination; the opening line of Pan Tadeusz ("Lithuania, my homeland…") anchors him in Lithuanian cultural memory.',
    be: 'Adam Mickievič — born to a Polonised gentry family in the Belarusian lands of the former Grand Duchy; his folklore motifs and birthplace near Navahrudak make him a foundational figure of Belarusian literature.',
    ltSource: 'Šeina, V. "The Lithuanianisation of Adam Mickiewicz." Interlitteraria 23 (2018)',
    beSource: 'Konan, U. Belarusian Literary Tradition. Minsk: NAS Belarus, 2010',
    neutral: 'Mickiewicz wrote in Polish, was born in present-day Belarus, and used "Lithuania" in the broader Commonwealth sense; the Cambridge History of Russia calls his ethnic origins "Lithuanian-Belarusian (and perhaps Jewish)".',
    sourceQuote: '"Mickiewicz is regarded as a national poet in Poland, Lithuania and Belarus." — Wikipedia / Cambridge History of Russia',
    correctAnswer: 2
  },
  {
    topic: 'Pahonia / Vytis Coat of Arms',
    lt: 'Vytis (the mounted knight) is among Europe\'s oldest heraldic symbols, in use as the Grand Duchy\'s arms since the late 14th century and the official emblem of the Republic of Lithuania today.',
    be: 'Pahonia is the historic coat of arms of the Belarusian lands within the GDL; adopted by the BNR in 1918 and used by independent Belarus from 1991 to 1995, when Lukashenko replaced it with a Soviet-style emblem.',
    ltSource: 'Madeinvilnius.lt: Heraldry Commission Chair on the Vytis (2022)',
    beSource: 'Rada BNR: "Pahonia, the Coat of Arms of Belarus" (radabnr.org)',
    neutral: 'The same heraldic device is the legitimate inheritance of every people of the former GDL; today it functions simultaneously as the Lithuanian state emblem and as a symbol of the Belarusian democratic opposition.',
    sourceQuote: '"Belarusians have right to use the armoured knight symbol, says heraldry specialist." — LRT (2023)',
    correctAnswer: 2
  },
  {
    topic: 'Tadeusz Kościuszko / Tadevuš Kasciuška',
    lt: 'Tadas Kosciuška — a leader of the 1794 uprising and a son of the Polish-Lithuanian Commonwealth, commemorated in Lithuania as a defender of Commonwealth statehood against Russian and Prussian partition.',
    be: 'Tadevuš Kasciuška — born in 1746 at Mieračoŭščyna in the Brest region of present-day Belarus; the Belarusian diaspora funds monuments to him as an "outstanding son of Belarus".',
    ltSource: 'Britannica: "Tadeusz Kosciuszko" biography',
    beSource: 'Belarus.by: "Tadeusz Kosciuszko, Belarus"; BelarusDigest (2017)',
    neutral: 'Kościuszko is a national hero in Poland, the United States, Lithuania, and Belarus — his birthplace in the multi-ethnic Commonwealth makes all four claims historically grounded.',
    sourceQuote: '"Kościuszko was born in February 1746 in a manor on the Mereczowszczyzna estate… now the Ivatsevichy District of Belarus." — Wikipedia',
    correctAnswer: 2
  },
  {
    topic: 'Battle of Orsha (1514)',
    lt: 'A decisive Lithuanian victory under Grand Hetman Konstanty Ostrogski against Muscovy, halting Muscovite expansion and securing the eastern frontier of the Grand Duchy.',
    be: 'A battle fought on Belarusian soil under a commander of Ruthenian Orthodox lineage; Belarusian historiography frames it as a defence of Belarusian-Lithuanian statehood against Moscow.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'NAS Belarus, Institute of History (2014, 500th anniversary)',
    neutral: 'The 8 September 1514 victory was won by a combined Lithuanian-Polish force; Ostrogski, an Orthodox magnate from the Ruthenian lands, founded the Holy Trinity Church in Vilnius from the spoils.',
    sourceQuote: '"The Battle of Orsha was fought… under the command of Lithuanian Grand Hetman Konstanty Ostrogski." — Wikipedia',
    correctAnswer: 2
  },
  {
    topic: 'Vilnius Dispute (1920–1939)',
    lt: 'Vilnius was Lithuania\'s historic and rightful capital, seized illegally by Polish forces in Żeligowski\'s staged mutiny of October 1920; its return in 1939 restored a historical wrong.',
    be: 'Vilnia/Wilno was a multinational city in which Belarusians, Jews, Poles, Lithuanians and Russians all had deep roots; the interwar Polish-Lithuanian quarrel obscured that plurality.',
    ltSource: 'Lithuanian Institute of History',
    beSource: 'Snyder, T. The Reconstruction of Nations. Yale UP, 2003',
    neutral: 'Żeligowski\'s "mutiny" was secretly authorised by Piłsudski; Vilnius then sat inside Poland as Wilno Voivodeship from 1922 until 1939, while Lithuania kept Kaunas as a provisional capital.',
    sourceQuote: '"Żeligowski\'s forces marched on Vilnius and captured it one day before the Suwałki Agreement was to formally come into effect." — Wikipedia',
    correctAnswer: 2
  },
  {
    topic: 'Kastus Kalinouski / Konstanty Kalinowski',
    lt: 'Konstantinas Kalinauskas — a leader of the 1863 uprising on Lithuanian soil, fighting for the restoration of Commonwealth statehood against Russian rule; hanged in Vilnius in March 1864.',
    be: 'Kastuś Kalinoŭski — founding father of Belarusian nationalism, publisher of Mužyckaja Praŭda (the first Belarusian-language newspaper) and author of the prison "Letters from beneath the gallows".',
    ltSource: 'Snyder, T. The Reconstruction of Nations. Yale UP, 2003',
    beSource: 'BelarusDigest: "Konstanty Kalinouski: A Contested Hero"',
    neutral: 'Kalinouski is honoured as a national hero in Belarus, Poland and Lithuania; he wrote in Belarusian for peasants and in Polish for the gentry, mobilising both for the cause.',
    sourceQuote: '"I say to you from beneath the gallows, my People, that only then will you live happily, when no Muscovite remains over you." — Kalinouski, 1864',
    correctAnswer: 2
  },
  {
    topic: 'Holocaust in the Bloodlands',
    lt: 'The Holocaust in Lithuania saw roughly 95% of the pre-war Jewish community of ~210,000 murdered between 1941 and 1944, with significant local collaboration documented by post-1991 commissions.',
    be: 'Belarus lost approximately one quarter of its total population in WWII — among Europe\'s heaviest per-capita losses — including nearly all of its ~800,000 Jews, in killing fields, ghettos and burned villages.',
    ltSource: 'International Commission for the Evaluation of the Crimes of the Nazi and Soviet Occupation Regimes in Lithuania',
    beSource: 'Yad Vashem; Khatyn State Memorial Complex',
    neutral: 'Both republics sat at the heart of what Timothy Snyder calls the "bloodlands" — the zone between Berlin and Moscow where Nazi and Soviet regimes together killed some 14 million civilians between 1933 and 1945.',
    sourceQuote: '"The bloodlands… now comprises Poland, Belarus, Ukraine, the Baltic states… and the westernmost fringes of Russia." — Snyder, Bloodlands (2010)',
    correctAnswer: 2
  },
  {
    topic: '1995 Belarusian Referendum',
    lt: 'The 1995 referendum showed how easily a young post-Soviet state could be steered away from European integration; Lithuania, by contrast, was already negotiating EU accession by 1995.',
    be: 'The referendum stripped Belarusian of sole state-language status, restored Soviet-style symbols, and discarded Pahonia and the white-red-white flag — a decisive rupture with the 1991 national revival.',
    ltSource: 'Lithuanian Foreign Policy Review (1996)',
    beSource: 'Euroradio.fm: "The day Belarus lost its language, white-red-white flag and Pahonia coat of arms"',
    neutral: 'On 14 May 1995, 83.3% voted to grant Russian equal state-language status; the OSCE Parliamentary Assembly noted the referendum "violated international standards" given media control and obstacles to the opposition.',
    sourceQuote: '"The greatest support Lukashenka received was on giving the Russian language the status of a state language… 83.3% in favor." — Wikipedia',
    correctAnswer: 1
  }
];

/**
 * Get a random perspective challenge
 */
export function getRandomPerspective(): Perspective {
  return PERSPECTIVES[Math.floor(Math.random() * PERSPECTIVES.length)];
}

/**
 * Get perspective by topic
 */
export function getPerspectiveByTopic(topic: string): Perspective | undefined {
  return PERSPECTIVES.find(p => p.topic === topic);
}

/**
 * Validate a perspective answer
 * @param perspective The perspective challenge
 * @param answer 0=Lithuanian, 1=Belarusian, 2=Neutral
 */
export function validatePerspectiveAnswer(
  perspective: Perspective,
  answer: 0 | 1 | 2
): boolean {
  return perspective.correctAnswer === answer;
}

/**
 * Get answer label for display
 */
export function getPerspectiveAnswerLabel(answer: 0 | 1 | 2): string {
  switch (answer) {
    case 0:
      return 'Lithuanian';
    case 1:
      return 'Belarusian';
    case 2:
      return 'Neutral / Both';
    default:
      return 'Unknown';
  }
}

/**
 * Get all perspective topics
 */
export function getPerspectiveTopics(): string[] {
  return PERSPECTIVES.map(p => p.topic);
}
